import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Text,
  Avatar,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
} from "@chakra-ui/react";
import { PiStethoscopeBold } from "react-icons/pi";
import DoctorDetails from "./DoctorDetails";
import {
  useBannedPatientsLazyQuery,
} from "../graphql/types-and-hooks";
import { useBookAppointmentStore } from "../store";
import queryString from "query-string";
import { appointmentTypesStore } from "../store/AppointmentTypesStore";
import { calculateAgeFromDOB } from '../utils/dates';
import { isAgeInRange } from '../utils/isAgeInRange';
import { sessionManager } from '../managers/SessionManager';
import { bookingFlowStore } from '../store/BookingFlowStore';
import { formatDate, logerror, toISOString } from '../lib/util'; 
import { observer } from "mobx-react-lite"; // Import observer
import { runInAction } from "mobx"; // Import runInAction for proper MobX handling
import { logError } from "../components/logger";
import { doctorsStore } from "../store/DoctorsStore";

// eslint-disable-next-line no-restricted-globals
const parsed = queryString.parse(location.search);

// Access environment variables
const USER_FK = parsed.cid;
const CLINIC_GROUP_CRM = parsed.gid;
const LOCATION_ID = parsed.lid;

interface Doctor {
  display_name: string;
  about: string;
  image: string;
  gender: string;
  doctor_id: string;
  my_patients: string;
  display_only: string;
  no_new_patients: string;
}

interface dateandtime {
  fadate: string;
  fatime: string;
}

interface DoctorCardProps {
  doctor: Doctor;
  ddate: dateandtime;
  duration: string;
}

// Wrap the component with observer
const DoctorCard: React.FC<DoctorCardProps> = observer(({ doctor, ddate, duration }) => {
  const { display_name, about, image, gender } = doctor;
  const { fadate, fatime } = ddate;

  const originalHtml = about;
  const [isDoctorDetailsOpen, setIsDoctorDetailsOpen] = useState(false);

  const [
    bannedPatientsQuery,
  ] = useBannedPatientsLazyQuery();

  // eslint-disable-next-line
  const [patient, doctor1, serviceType, appointmentType, setData] = useBookAppointmentStore(
    (state) => [
      state.patient,
      state.doctor,
      state.serviceType,
      state.appointmentType,
      state.setData,
      state.resetData,
    ]
  );


  const [day, month, year] = fadate.split("-");

  // Extract observable value at component level where it's tracked by observer
  const selectedAptType = appointmentTypesStore.selectedAptType;

  const [hours, minutes] = fatime.split(":");

  const isoDateString = `${year}-${month}-${day}T${hours}:${minutes}:00`;

  const dateObj = new Date(isoDateString);

  const dateInput = dateObj;
  const formattedDate = formatDate(toISOString(dateInput));

  const mapStringToValue = (input) => {
    switch (input) {
      case "F":
        return "Female";
      case "M":
        return "Male";
      default:
        return input;
    }
  };

  const originalString = gender;
  const mappedValue = mapStringToValue(originalString);

  const [isBanned, setIsBanned] = useState(false);
  const [isAgeValid, setIsAgeValid] = useState(true);
  // Store the no_new_patients value in local state to avoid direct observable access
  const [, setIsNoNewPatients] = useState(false);

  const validatePatient = useCallback(async () => {
    if (!patient) return;
    try {
      const result = await bannedPatientsQuery({
        variables: {
          input: {
            clinic_group_crm: CLINIC_GROUP_CRM,
            user_fk: USER_FK,
            location_id: LOCATION_ID,
            patient_id: sessionManager.patientId,
          },
        },
      });

      if (sessionManager.patientId) {
        const bannedLevel = parseInt(
          result?.data?.bannedPatients.banned_level || "0"
        );
        if (bannedLevel !== 0) {
          setIsBanned(true);
          return;
        }
      }

      const patientAge = calculateAgeFromDOB(patient.dob);

      // Use the extracted selectedAptType value instead of accessing the store directly
      if (
        !isAgeInRange(patientAge, {
          low_age_min: selectedAptType?.low_age_min,
          low_age_max: selectedAptType?.low_age_max,
          upper_age_min: selectedAptType?.upper_age_min,
          upper_age_max: selectedAptType?.upper_age_max,
        })
      ) {
        setIsAgeValid(false);
        return;
      }
    } catch (error) {
      logError("validatePatientError", error);
      logerror("Validation Error:", error);
    }
  }, [bannedPatientsQuery, patient, selectedAptType]); // Add selectedAptType to dependencies

  const handleOpenDrawer = () => {
    setData({ doctor: doctor });
    bookingFlowStore.resetFlow();
    bookingFlowStore.updateAppointmentData("selectedDoctor", doctor);
    setIsDoctorDetailsOpen(true);
  };

  const handleCloseDrawer = () => {
    bookingFlowStore.resetFlow();
    setIsDoctorDetailsOpen(false);
  };

  const makeAppointment = () => {
    doctorsStore.setCurrentDoctor(doctor);
    bookingFlowStore.updateAppointmentData("selectedDoctor", doctor);
    setData({ doctor: doctor });
    bookingFlowStore.setIsModalOpen(true);
  };

  useEffect(() => {
    validatePatient();
    
    // Use runInAction to properly track observable access
    runInAction(() => {
      if (doctor && typeof doctor.no_new_patients === 'string') {
        setIsNoNewPatients(doctor.no_new_patients === "1");
      }
    });
  }, [validatePatient, doctor]);

  // Directly use the observable value within render - this is tracked properly
  const isExistingPatientOfDoctor = patient?.last_seen_did === doctor?.doctor_id;
  const showNoNewPatientsAlert = doctor.no_new_patients === "1" && !isExistingPatientOfDoctor;

  const isValidDate = (date) => {
    // Check if the date is not null, undefined, or an empty string
    if (!date) {
      return false;
    }

    // Parse the date
    const parsedDate = new Date(date);

    // Check if it's a valid date object (not NaN)
    if (!isNaN(parsedDate.getTime())) {
      return false;
    }

    // Hardcoded check to filter out "12:00 am Saturday 1st January 2000"
    const hardcodedInvalidDate = new Date("2000-01-01T00:00:00");
    if (parsedDate.getTime() === hardcodedInvalidDate.getTime()) {
      return false;
    }

    // Get today's date without the time part
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if the date is in the past (return false if it is)
    if (parsedDate < today) {
      return false;
    }

    // If all checks pass, return true
    return true;
  };

  const isFormattedDateValid = (
    isValidDate(formattedDate) &&
    formattedDate !== "12.00 am Sat 1st Jan 2000" &&
    formattedDate !== "12.00 am Sat 1st Jan 0" &&
    !formattedDate.includes("NaN") &&
    !formattedDate.includes("undefined")
  );

  return (
    <Box
      borderWidth="2px"
      borderRadius="xl"
      overflow="hidden"
      boxShadow="md"
      display="flex"
      flexDirection="row"
      justifyContent={"space-between"}
      mt={3}
    >
      <Box
        bg="#665F5F"
        width={["25px", "35px"]}
        fontSize={["15px", "20px"]}
        p={[1, 2, 2]}
        pt={14}
      >
        <PiStethoscopeBold color="#fff" />
      </Box>

      <Box width={"100%"}>
        <Box
          display="flex"
          flexDirection={["column", "column", "row"]}
          justifyContent={"space-between"}
          width={"100%"}
        >
          <Box p={4} display="flex" flexDirection={["row"]} onClick={handleOpenDrawer} cursor="pointer">
            <Avatar
              name={display_name}
              size="xl"
              src={image}
              style={{ boxShadow: "5px 5px 5px #bbb" }}
            >
            </Avatar>
            <Box ml={4}>
              <Text fontWeight="bold">{display_name}</Text>

              <p
                style={{
                  fontSize: "12px",
                  color: "gray",
                  height: "35px",
                  overflow: "hidden",
                  maxWidth: "350px",
                }}
                dangerouslySetInnerHTML={{ __html: originalHtml }}
              />

              <Text fontSize="xs" color="gray">
                {mappedValue}
              </Text>

              <Box display="flex" alignItems="center">
                <Button onClick={handleOpenDrawer} size="xs" fontSize="x-small" variant="ghost"
                  _hover={{ bg: "#B92F2F", color: "white" }}
                  color="#B92F2F" borderRadius="full">
                  <Text as="u">More Details</Text>
                </Button>
                <DoctorDetails
                  doctor={doctor}
                  isOpen={isDoctorDetailsOpen}
                  onClose={handleCloseDrawer}
                />
              </Box>
            </Box>
          </Box>
          <Box
            p={[2, 4, 4, 4, 4]}
            display="flex"
            flexDirection={["column", "column", "column", "column"]}
            gap={"10px"}
          >
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              minWidth={'275px'}
              justifyContent={"space-between"}
            >
              <Box
                borderRadius="full"
                height="41px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
              >
                <Box pl="10px" pr="20px" textAlign="center">
                  <Text fontSize="sm" as="b" color="#B92F2F">
                    {isFormattedDateValid ? formattedDate : "No time slots available"}
                  </Text>
                  <Text fontSize="11px" mt="-3px" fontWeight="bold" color="#665F5F">
                    First Available Time
                  </Text>
                </Box>
              </Box>

            </Box>

            <Box
              display="flex"
              alignItems="center"
              width={"100%"}
              justifyContent={"space-between"}
            >

              <Button
                onClick={makeAppointment}
                fontSize="small"
                variant="outline"
                border="2px"
                borderColor="#B92F2F"
                color="#B92F2F"
                _hover={{ bg: "#B92F2F", color: "#fff" }}
                width={"100%"}
                borderRadius="30px"
              >
                Book this Doctor
              </Button>

            </Box>
          </Box>
        </Box>

        {showNoNewPatientsAlert && (
          <Alert status="info">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>
                {`${display_name} is not available for new patients. `}{" "}
                {!patient && "Please login to confirm you are an existing patient to proceed to booking"}
              </AlertTitle>
            </Box>
          </Alert>
        )}

        {patient && (isBanned || !isAgeValid) && (
          <Alert status="error">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>
                {isBanned &&
                  "We are not able to complete your booking online. Please call the clinic to make an appointment."}
                {!isAgeValid && `${display_name} is not available to book this particular appointment. Please choose an alternative doctor. `}
              </AlertTitle>
            </Box>
          </Alert>
        )}
      </Box>
    </Box>
  );
});

export default DoctorCard;